import React from 'react';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import PropTypes from 'prop-types';
import COIManager from './COI/COIManager';
import CommitteeFilter from './CommitteeFilter';
import { useCouncilMemberNominations } from '../../hooks/useCouncilMemberNominations';

const FilterControls = ({
  isAdmin,
  isSCChair,
  isSCObserver,
  isSCSecretary,
  isSCMember,
  isCouncilMember,
  showCOIFeatures,
  selectedCommittee,
  sectionalCommitteesWithCmse,
  handleCommitteeChange,
  selectedNominationStatus,
  setSelectedStatus,
  selectedRankingFilter,
  setSelectedRankingFilter,
  selectedCOICandidate,
  setSelectedCOICandidate,
  userDetails,
  setNotificationTitle,
  setNotificationMessage,
  showMyAllocations,
  setShowMyAllocations,
  getCommitteeCandidateCount,
  enableRecommendedToCouncil,
  recommendedToCouncilCount,
  rankedCount,
  overlapNominationsCount,
}) => {
  const councilMemberNominations = useCouncilMemberNominations(
    userDetails,
    isCouncilMember,
    !isAdmin,
  );

  const hasAllocations =
    isCouncilMember && !isAdmin && councilMemberNominations.allocatedCount > 0;

  const getShortlistStatusTitle = () => {
    if (showMyAllocations && hasAllocations) {
      return 'My Allocations';
    }
    if (selectedRankingFilter === 'ranked') {
      return 'Ranked';
    }
    if (selectedRankingFilter === 'unranked') {
      return 'Not Ranked';
    }
    if (selectedRankingFilter === 'overlap') {
      return 'Overlapped Nominations';
    }

    const statusLabels = {
      3: 'Shortlisted',
      8: 'Recommended to Council',
      2: 'Not Shortlisted',
    };

    if (!selectedNominationStatus || selectedNominationStatus.length === 0) {
      return 'Filter by Status';
    }

    return statusLabels[selectedNominationStatus[0]] || 'Filter by Status';
  };

  const handleStatusClick = (status) => {
    setSelectedStatus(status);
    setSelectedRankingFilter(null);
    setShowMyAllocations(false);
  };

  const handleRankingClick = (rankingValue) => {
    setSelectedRankingFilter(rankingValue);
    setSelectedStatus(null);
    setShowMyAllocations(false);
  };

  const handleMyAllocationsClick = () => {
    const newValue = !showMyAllocations;
    setShowMyAllocations(newValue);
    setSelectedStatus(null);
    setSelectedRankingFilter(null);

    if (newValue) {
      handleCommitteeChange(null);
    }
  };

  const handleCommitteeSelect = (committeeId) => {
    handleCommitteeChange(committeeId);
    if (showMyAllocations) {
      setShowMyAllocations(false);
    }
  };

  const isAllRanked = recommendedToCouncilCount - rankedCount === 0;

  return (
    <div className="table-controls mt-xl-5 mb-4 d-flex align-items-center gap-2">
      {(isAdmin || isSCSecretary || isCouncilMember) && (
        <CommitteeFilter
          selectedCommittee={selectedCommittee}
          onCommitteeChange={handleCommitteeSelect}
          committees={sectionalCommitteesWithCmse}
          getCount={getCommitteeCandidateCount}
        />
      )}

      {(isAdmin ||
        isSCChair ||
        isSCObserver ||
        isSCSecretary ||
        isSCMember ||
        isCouncilMember) && (
        <DropdownButton
          id="shortlist-status-dropdown"
          variant="outline-primary"
          title={getShortlistStatusTitle()}
        >
          <Dropdown.Item onClick={() => handleStatusClick(null)}>
            All
          </Dropdown.Item>
          {hasAllocations && (
            <>
              <Dropdown.Item
                onClick={handleMyAllocationsClick}
                active={showMyAllocations}
              >
                My Allocations
              </Dropdown.Item>
              <Dropdown.Divider />
            </>
          )}
          <Dropdown.Item onClick={() => handleStatusClick([3])}>
            Shortlisted
          </Dropdown.Item>
          {enableRecommendedToCouncil && (
            <Dropdown.Item
              onClick={() => handleStatusClick([8])}
              disabled={recommendedToCouncilCount === 0}
              className={recommendedToCouncilCount === 0 ? 'text-muted' : ''}
            >
              Recommended to Council{' '}
              {recommendedToCouncilCount > 0
                ? `(${recommendedToCouncilCount})`
                : '(0)'}
            </Dropdown.Item>
          )}
          <Dropdown.Item onClick={() => handleStatusClick([2])}>
            Not Shortlisted
          </Dropdown.Item>
          {enableRecommendedToCouncil && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={() => handleRankingClick('ranked')}
                disabled={rankedCount === 0}
                className={rankedCount === 0 ? 'text-muted' : ''}
              >
                Ranked{' '}
                {recommendedToCouncilCount > 0
                  ? `(${rankedCount}/${recommendedToCouncilCount})`
                  : '(0)'}
              </Dropdown.Item>
            </>
          )}

          {enableRecommendedToCouncil && !isAllRanked && (
            <Dropdown.Item onClick={() => handleRankingClick('unranked')}>
              Not Ranked ({recommendedToCouncilCount - rankedCount})
            </Dropdown.Item>
          )}

          {enableRecommendedToCouncil && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={() => handleRankingClick('overlap')}
                disabled={overlapNominationsCount === 0}
                className={overlapNominationsCount === 0 ? 'text-muted' : ''}
              >
                Overlapped Nominations ({overlapNominationsCount})
              </Dropdown.Item>
            </>
          )}
        </DropdownButton>
      )}

      {showCOIFeatures && (
        <COIManager
          selectedCandidate={selectedCOICandidate}
          onCandidateSelect={setSelectedCOICandidate}
          userDetails={userDetails}
          sectionalCommitteesWithCmse={sectionalCommitteesWithCmse}
          setNotificationTitle={setNotificationTitle}
          setNotificationMessage={setNotificationMessage}
        />
      )}
    </div>
  );
};

FilterControls.propTypes = {
  isAdmin: PropTypes.bool.isRequired,
  isSCChair: PropTypes.bool.isRequired,
  isSCObserver: PropTypes.bool.isRequired,
  isSCSecretary: PropTypes.bool,
  isSCMember: PropTypes.bool.isRequired,
  isCouncilMember: PropTypes.bool.isRequired,
  showCOIFeatures: PropTypes.bool.isRequired,
  selectedCommittee: PropTypes.number,
  sectionalCommitteesWithCmse: PropTypes.array.isRequired,
  handleCommitteeChange: PropTypes.func.isRequired,
  selectedNominationStatus: PropTypes.array,
  setSelectedStatus: PropTypes.func.isRequired,
  selectedRankingFilter: PropTypes.string,
  setSelectedRankingFilter: PropTypes.func.isRequired,
  selectedCOICandidate: PropTypes.object,
  setSelectedCOICandidate: PropTypes.func.isRequired,
  userDetails: PropTypes.object.isRequired,
  setNotificationTitle: PropTypes.func.isRequired,
  setNotificationMessage: PropTypes.func.isRequired,
  showMyAllocations: PropTypes.bool.isRequired,
  setShowMyAllocations: PropTypes.func.isRequired,
  getCommitteeCandidateCount: PropTypes.func,
  enableRecommendedToCouncil: PropTypes.bool,
  recommendedToCouncilCount: PropTypes.number,
  rankedCount: PropTypes.number,
  overlapNominationsCount: PropTypes.number,
};

export default FilterControls;
